/**
 * Auto-matching utilities for CSV header-to-column mapping.
 *
 * Fuzzy-matches CSV headers against known column patterns
 * with conflict resolution.
 */

interface HeaderRow {
  original: string
  mapped: string
  label?: string
}

function levenshteinDistance(str1: string, str2: string): number {
  const matrix = []

  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i]
  }

  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j
  }

  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1]
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        )
      }
    }
  }

  return matrix[str2.length][str1.length]
}

function stringSimilarity(str1: string, str2: string): number {
  const maxLength = Math.max(str1.length, str2.length)
  if (maxLength === 0) return 1

  const distance = levenshteinDistance(str1, str2)
  return 1 - distance / maxLength
}

/**
 * Match a single CSV header against known column patterns.
 *
 * Tries exact match first, then falls back to fuzzy matching
 * with a 0.6 similarity threshold.
 *
 * @param headerName - Raw CSV header string
 * @param patterns - Map of column keys to their known aliases
 * @returns Matched column key, or 'ignore' if no match found
 */
export function autoMatchHeader(
  headerName: string,
  patterns: Record<string, string[]>
): string {
  const normalizedHeader = headerName
    .toLowerCase()
    .replace(/ /g, '_') // Replace spaces with underscore
    .replace(/[^a-z0-9]/g, '_') // Replace special chars with underscore
    .replace(/_+/g, '_') // Collapse multiple underscores
    .replace(/^_|_$/g, '') // Trim leading/trailing underscores

  // Try exact keyword matches first
  for (const [columnKey, aliases] of Object.entries(patterns)) {
    if (aliases.some((alias) => normalizedHeader === alias)) {
      return columnKey
    }
  }

  // Try fuzzy matching with string similarity
  let bestMatch = 'ignore'
  let bestScore = 0.6 // Minimum confidence threshold

  for (const [columnKey, aliases] of Object.entries(patterns)) {
    for (const alias of aliases) {
      const score = stringSimilarity(normalizedHeader, alias)
      if (score > bestScore) {
        bestScore = score
        bestMatch = columnKey
      }
    }
  }

  return bestMatch
}

/**
 * Auto-match all headers with deduplication.
 *
 * Ensures each column key is used at most once (except company_notes
 * which can be mapped multiple times). Conflicts are resolved by
 * first-match-wins ordering.
 *
 * @param headers - Array of header rows to match
 * @param patterns - Map of column keys to their known aliases
 * @returns Headers with `mapped` field set to matched column or 'ignore'
 */
export function autoMatchWithConflictResolution(
  headers: HeaderRow[],
  patterns: Record<string, string[]>
): HeaderRow[] {
  const matches: Array<{
    header: HeaderRow
    column: string
    originalIndex: number
  }> = []

  // Get all possible matches
  headers.forEach((header, index) => {
    const column = autoMatchHeader(header.original, patterns)
    matches.push({ header, column, originalIndex: index })
  })

  // Sort by priority: non-ignore matches first, then by original order
  const finalMatches = new Map<string, string>()
  const usedColumns = new Set<string>()

  // First pass: handle non-ignore matches
  matches
    .filter((match) => match.column !== 'ignore')
    .forEach((match) => {
      if (!usedColumns.has(match.column) || match.column === 'company_notes') {
        finalMatches.set(match.header.original, match.column)
        if (match.column !== 'company_notes') {
          usedColumns.add(match.column)
        }
      } else {
        finalMatches.set(match.header.original, 'ignore')
      }
    })

  // Second pass: handle ignore matches
  matches
    .filter((match) => match.column === 'ignore')
    .forEach((match) => {
      finalMatches.set(match.header.original, 'ignore')
    })

  return headers.map((header) => ({
    ...header,
    mapped: finalMatches.get(header.original) || 'ignore',
  }))
}
